import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import Title from '@/components/ui/Title';
import { CircleAlert, Play, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import { UrlVariableInput } from '@/components/ui/UrlVariableInput';
import type { UseFormReturn } from 'react-hook-form';
import type { OfferFormValues } from './OfferForm';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import Loader from '@/components/ui/Loader';
import ColorSelector from '@/components/ui/ColorSelector';
import { cn } from '@/lib/utils';
import { useAppDispatch } from '@/app/hooks';
import {
  deleteOfferTemplateContentImage,
  fetchOfferTestButtonLink,
  fetchOfferTestEmbed,
  upsertOfferTemplateContent,
} from '@/features/webinar/webinarThunks';
import { toast } from 'react-toastify';
import {
  resolveOfferTestUrl,
  type OfferTemplateContent,
  type StripePaymentAvailability,
} from '@/features/webinar/types';
import OfferEmbedPreviewDialog from './OfferEmbedPreviewDialog';
import StripePricesField from './StripePricesField';
import type { AppError } from '@/features/auth/types';
import { mapOfferContentToInput } from './OffersForm';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { OFFER_EMBED_CODE_MAX_LENGTH, isOfferEmbedCodeRejected } from '@/lib/offerEmbed';
import { buildUrlVariables, insertAtSelection } from '@/lib/urlVariables';
import { isStripeConnectEnabled } from '@/lib/stripeConnect';
import { CodeFieldWithVariables } from '@/components/ui/CodeFieldWithVariables';

interface OfferFormProps {
  visualIdentity: WorkspaceVisualIdentity | null | undefined;
  form: UseFormReturn<OfferFormValues>;
  offerType: 'A' | 'B';
  offerIndex?: number;

  readOnly?: boolean;
  workspaceId?: number;
  webinarId?: number;
  templateId?: number;
  stripePaymentAvailability?: StripePaymentAvailability | null;
  // Le back refuse le paiement Stripe sur un webinaire Evergreen : il n'est payable qu'en live.
  stripeCheckoutAllowed?: boolean;
}

export default function OfferFormContent({
  visualIdentity,
  form,
  offerType,
  offerIndex = 0,

  readOnly,
  workspaceId,
  webinarId,
  templateId,
  stripePaymentAvailability,
  stripeCheckoutAllowed = true,
}: OfferFormProps) {
  const dispatch = useAppDispatch();
  const fileInputId = useMemo(
    () => `offer-file-upload-${offerIndex}-${offerType}`,
    [offerIndex, offerType],
  );
  const locked = readOnly === true;
  const { t } = useTranslation('createWebinar');
  const [isTestingLink, setIsTestingLink] = useState(false);

  const conversionMode = form.watch(`conversionMode${offerType}`);
  const isEmbedMode = conversionMode === 'EMBED';
  const isStripeCheckoutMode = conversionMode === 'STRIPE_EMBEDDED_CHECKOUT';
  const embedCodeFieldRef = useRef<HTMLDivElement>(null);
  const urlVariables = buildUrlVariables(t);

  // La zone de texte garde sa sélection après le clic sur la variable : on insère au curseur.
  const handleInsertEmbedVariable = (variable: string) => {
    const fieldName = `embedCode${offerType}` as const;
    const textarea = embedCodeFieldRef.current?.querySelector('textarea');
    const current = String(form.getValues(fieldName) ?? '');
    const { value, caret } = insertAtSelection(
      current,
      textarea?.selectionStart ?? current.length,
      textarea?.selectionEnd ?? current.length,
      variable,
    );
    form.setValue(fieldName, value, { shouldDirty: true, shouldValidate: true });
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(caret, caret);
    });
  };
  const buttonLinkFieldName = `buttonLink${offerType}` as const;
  const isButtonLinkDirty = Boolean(form.formState.dirtyFields[buttonLinkFieldName]);
  const embedCodeFieldName = `embedCode${offerType}` as const;
  const [isTestingEmbed, setIsTestingEmbed] = useState(false);
  const [embedPreview, setEmbedPreview] = useState<OfferTemplateContent | null>(null);

  // Le back n'aperçoit que la variante enregistrée : un code modifié est d'abord sauvegardé.
  const handleTestOfferEmbed = async () => {
    if (locked) return;
    if (!workspaceId || !webinarId || !templateId) {
      toast.error(t('offersForm.content.testEmbedNotSaved'));
      return;
    }
    const currentCode = String(form.getValues(embedCodeFieldName) ?? '').trim();
    if (!currentCode) {
      toast.error(t('offersForm.content.testEmbedEmpty'));
      return;
    }

    setIsTestingEmbed(true);
    try {
      const dirtyFields = form.formState.dirtyFields;
      if (dirtyFields[embedCodeFieldName] || dirtyFields[`conversionMode${offerType}`]) {
        const isFieldValid = await form.trigger(embedCodeFieldName);
        if (!isFieldValid) return;
        await dispatch(
          upsertOfferTemplateContent({
            workspaceId,
            webinarId,
            templateId,
            contentType: offerType,
            input: mapOfferContentToInput(form.getValues(), offerType, visualIdentity),
          }),
        ).unwrap();
        form.resetField(embedCodeFieldName, { defaultValue: form.getValues(embedCodeFieldName) });
        form.resetField(`conversionMode${offerType}`, {
          defaultValue: form.getValues(`conversionMode${offerType}`),
        });
      }

      const content = await dispatch(
        fetchOfferTestEmbed({ workspaceId, webinarId, templateId, contentType: offerType }),
      ).unwrap();
      setEmbedPreview(content);
    } catch (err) {
      const appErr = err as AppError;
      if (isOfferEmbedCodeRejected(appErr)) {
        form.setError(embedCodeFieldName, {
          type: 'server',
          message: t('errors.offerEmbedCodeInvalid'),
        });
        toast.error(t('errors.offerEmbedCodeInvalid'));
        return;
      }
      const message =
        appErr?.status === 403
          ? t('offersForm.content.testEmbedForbidden')
          : appErr?.status === 404
            ? t('offersForm.content.testUrlNotFound')
            : t('offersForm.content.testEmbedError');
      toast.error(message);
    } finally {
      setIsTestingEmbed(false);
    }
  };

  const handleTestOfferUrl = async () => {
    if (locked) return;
    if (!workspaceId || !webinarId || !templateId) {
      toast.error(t('offersForm.content.testUrlNotSaved'));
      return;
    }

    const currentUrl = String(form.getValues(buttonLinkFieldName) ?? '').trim();
    if (!currentUrl) {
      toast.error(t('offersForm.content.testUrlEmpty'));
      return;
    }
    const testWindow = window.open('about:blank', '_blank');
    if (testWindow) testWindow.opener = null;

    setIsTestingLink(true);
    try {
      if (isButtonLinkDirty) {
        const isFieldValid = await form.trigger(buttonLinkFieldName);
        if (!isFieldValid) {
          return;
        }
        await dispatch(
          upsertOfferTemplateContent({
            workspaceId,
            webinarId,
            templateId,
            contentType: offerType,
            input: mapOfferContentToInput(form.getValues(), offerType, visualIdentity),
          }),
        ).unwrap();
        form.resetField(buttonLinkFieldName, { defaultValue: currentUrl });
      }

      const result = await dispatch(
        fetchOfferTestButtonLink({
          workspaceId,
          webinarId,
          templateId,
          contentType: offerType,
        }),
      ).unwrap();
      const url = resolveOfferTestUrl(result);
      if (!url) {
        testWindow?.close();
        toast.error(t('offersForm.content.testUrlEmpty'));
        return;
      }
      if (testWindow) {
        testWindow.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      toast.success(t('offersForm.content.testUrlSuccess'));
    } catch (err) {
      testWindow?.close();
      const appErr = err as AppError;
      const details = (Array.isArray(appErr?.details) ? appErr.details : []) as Array<{
        message?: string;
      }>;
      const isButtonLinkInvalid =
        appErr?.message === 'HTTP.ERROR.400.OFFER.CONTENT.BUTTON_LINK.INVALID' ||
        details.some((d) => d?.message === 'HTTP.ERROR.400.OFFER.CONTENT.BUTTON_LINK.INVALID');
      if (isButtonLinkInvalid) {
        form.setError(buttonLinkFieldName, {
          type: 'server',
          message: t('offersForm.content.testUrlLinkInvalid'),
        });
        toast.error(t('offersForm.content.testUrlLinkInvalid'));
        return;
      }

      const message =
        appErr?.status === 403
          ? t('offersForm.content.testUrlForbidden')
          : appErr?.status === 404
            ? t('offersForm.content.testUrlNotFound')
            : t('offersForm.content.testUrlError');
      toast.error(message);
    } finally {
      setIsTestingLink(false);
    }
  };

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFilePreviewUrl, setSelectedFilePreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    const imageValue = form.watch(`img${offerType}`);
    const showImageValue = form.watch(`showImage${offerType}`);

    if (
      imageValue &&
      typeof imageValue === 'string' &&
      imageValue.trim() !== '' &&
      !showImageValue
    ) {
      form.setValue(`showImage${offerType}`, true);
    }
  }, [form, offerType]);

  useEffect(() => {
    if (!selectedFile) {
      setSelectedFilePreviewUrl(null);
      return;
    }

    const previewUrl = URL.createObjectURL(selectedFile);
    setSelectedFilePreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [selectedFile]);

  const getColorValueById = (colorId: string, customColor?: string) => {
    if (colorId === 'no-border') {
      return 'transparent';
    }
    if (colorId === 'custom') {
      return customColor || '#FFFFFF';
    }
    const color = colorsArray.find((c) => c.id === colorId);
    return color?.value || '#FFFFFF';
  };

  const getBackgroundStyle = (colorId: string, customColor?: string) => {
    if (colorId === 'gradient1-color1' || colorId === 'gradient1-color2') {
      return {
        background: `linear-gradient(to bottom, ${visualIdentity?.gradient1Color1}, ${visualIdentity?.gradient1Color2})`,
      };
    }
    if (colorId === 'gradient2-color1' || colorId === 'gradient2-color2') {
      return {
        background: `linear-gradient(226deg, ${visualIdentity?.gradient2Color1} 0.2%, ${visualIdentity?.gradient2Color2} 104.63%)`,
      };
    }
    return {
      backgroundColor: getColorValueById(colorId, customColor),
    };
  };

  const getBorderStyle = (colorId: string, customColor?: string) => {
    if (colorId === 'no-border') {
      return {
        border: 'none',
      };
    }
    return {
      borderColor: getColorValueById(colorId, customColor),
    };
  };

  const getFinalColor = (
    colorType:
      | `backgroundColor${'A' | 'B'}`
      | `borderColor${'A' | 'B'}`
      | `titleColor${'A' | 'B'}`
      | `buttonColor${'A' | 'B'}`
      | `buttonTextColor${'A' | 'B'}`
      | `iconColor${'A' | 'B'}`
      | `iconBackgroundColor${'A' | 'B'}`,
  ) => {
    const colorId = form.watch(colorType);
    const customColorField =
      `custom${colorType.charAt(0).toUpperCase() + colorType.slice(1)}` as any;
    const customColor = form.watch(customColorField);

    if (colorId === 'custom') {
      return customColor || '#000000';
    }

    return getColorValueById(colorId || 'accent', customColor);
  };

  const colorsArray = useMemo(
    () => [
      {
        id: 'accent',
        name: t('offersForm.content.colors.accent'),
        value: visualIdentity?.accentColor,
        color: visualIdentity?.accentColor,
      },
      {
        id: 'background',
        name: t('offersForm.content.colors.background'),
        value: visualIdentity?.backgroundColor,
        color: visualIdentity?.backgroundColor,
      },
      {
        id: 'contrast',
        name: t('offersForm.content.colors.contrast'),
        value: visualIdentity?.contrastColor,
        color: visualIdentity?.contrastColor,
      },
      {
        id: 'gradient1-color1',
        name: t('offersForm.content.colors.gradient1'),
        value: visualIdentity?.gradient1Color1,
        color: visualIdentity?.gradient1Color1,
      },
      {
        id: 'gradient2-color1',
        name: t('offersForm.content.colors.gradient2'),
        value: visualIdentity?.gradient2Color1,
        color: visualIdentity?.gradient2Color1,
      },
      {
        id: 'secondary-background',
        name: t('offersForm.content.colors.secondaryBackground'),
        value: visualIdentity?.secondaryBackgroundColor,
        color: visualIdentity?.secondaryBackgroundColor,
      },
      {
        id: 'black',
        name: t('offersForm.content.colors.black'),
        value: '#000000',
        color: '#000000',
      },
      {
        id: 'white',
        name: t('offersForm.content.colors.white'),
        value: '#FFFFFF',
        color: '#FFFFFF',
      },
    ],
    [visualIdentity, t],
  );

  const arrayFontColors = useMemo(
    () => [
      {
        name: t('offersForm.content.colors.black'),
        value: '#000000',
      },
      {
        name: t('offersForm.content.colors.white'),
        value: '#FFFFFF',
      },
    ],
    [t],
  );
  const imageValue = form.watch(`img${offerType}`);
  const existingImageUrl =
    typeof imageValue === 'string' && imageValue.trim() !== '' ? imageValue : null;
  const previewImageUrl = selectedFilePreviewUrl || existingImageUrl;

  return (
    <div
      className={cn('flex flex-col gap-4', locked && 'pointer-events-none select-none opacity-90')}
    >
      <FormField
        name={`title${offerType}`}
        control={form.control}
        render={({ field, fieldState }) => (
          <FormItem>
            <TextFieldWithLimit
              label={t('offersForm.content.titleLabel')}
              value={field.value}
              onChange={(v) => field.onChange(v)}
              className="bg-white"
              maxLength={35}
              error={fieldState.error?.message}
            />
          </FormItem>
        )}
      />

      <FormField
        name={`subTitle${offerType}`}
        control={form.control}
        render={({ field, fieldState }) => (
          <FormItem>
            <TextFieldWithLimit
              label={t('offersForm.content.subtitleLabel')}
              value={field.value ?? ''}
              onChange={(v) => field.onChange(v || null)}
              className="bg-white"
              maxLength={35}
              error={fieldState.error?.message}
            />
          </FormItem>
        )}
      />

      <FormField
        name={`description${offerType}`}
        control={form.control}
        render={({ field, fieldState }) => (
          <FormItem>
            <TextFieldWithLimit
              label={t('offersForm.content.descriptionLabel')}
              value={field.value ?? ''}
              onChange={(v) => field.onChange(v || null)}
              className="bg-white"
              maxLength={100}
              error={fieldState.error?.message}
            />
          </FormItem>
        )}
      />

      <Title level={2}>{t('offersForm.content.buttonSectionTitle')}</Title>

      <div className="flex flex-col items-center gap-4 w-full">
        <div className="w-full grid gap-4 md:grid-cols-2">
          <FormField
            name={`buttonLabel${offerType}`}
            control={form.control}
            render={({ field, fieldState }) => (
              <FormItem>
                <TextFieldWithLimit
                  className="w-full bg-white"
                  label={t('offersForm.content.buttonTextLabel')}
                  value={field.value ?? ''}
                  onChange={(v) => field.onChange(v)}
                  maxLength={35}
                  error={fieldState.error?.message}
                />
              </FormItem>
            )}
          />
          <FormField
            name={`conversionMode${offerType}`}
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <Select
                  value={field.value ?? 'EXTERNAL_LINK'}
                  onValueChange={(value) => {
                    field.onChange(value);
                    void form.trigger([
                      `buttonLink${offerType}`,
                      `embedCode${offerType}`,
                      `stripePrices${offerType}`,
                    ]);
                  }}
                  disabled={locked}
                >
                  <SelectTrigger label={t('offersForm.content.redirectionTypeLabel')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EXTERNAL_LINK">
                      {t('offersForm.content.redirectionTypeLink')}
                    </SelectItem>
                    <SelectItem value="EMBED">
                      {t('offersForm.content.redirectionTypeEmbed')}
                    </SelectItem>
                    {((stripeCheckoutAllowed && isStripeConnectEnabled()) ||
                      isStripeCheckoutMode) && (
                      <SelectItem value="STRIPE_EMBEDDED_CHECKOUT">
                        {t('offersForm.content.redirectionTypeStripe')}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        </div>
        {isEmbedMode ? (
          <div className="w-full">
            <FormField
              name={`embedCode${offerType}`}
              control={form.control}
              render={({ field, fieldState }) => (
                <FormItem ref={embedCodeFieldRef}>
                  <CodeFieldWithVariables
                    label={t('offersForm.content.embedCodeLabel')}
                    value={field.value ?? ''}
                    onChange={(v) => field.onChange(v)}
                    variableValues={urlVariables.map((variable) => variable.value)}
                    maxLength={OFFER_EMBED_CODE_MAX_LENGTH}
                    disabled={locked}
                    error={fieldState.error?.message}
                  />
                  <p className="text-xs text-tertiary px-1">
                    {t('offersForm.content.embedCodeHint')}
                  </p>
                  <p className="flex items-center gap-1 text-xs text-secondary">
                    <CircleAlert size={16} /> {t('offersForm.content.embedVariablesHint')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {urlVariables.map((variable) => (
                      <button
                        key={variable.value}
                        type="button"
                        disabled={locked}
                        onClick={() => handleInsertEmbedVariable(variable.value)}
                        className="px-3 py-1 rounded-md bg-decorative-1 cursor-pointer hover:bg-decorative-1/80 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {variable.label}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={handleTestOfferEmbed}
                    disabled={isTestingEmbed || !templateId || locked}
                    className="mt-2 self-start bg-primary/10 text-primary rounded-lg py-2 px-3 border border-primary/20 cursor-pointer hover:bg-primary/20 h-10 flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      templateId
                        ? t('offersForm.content.testEmbedTooltip')
                        : t('offersForm.content.testEmbedNotSaved')
                    }
                  >
                    {isTestingEmbed ? <Loader /> : <Play className="size-4 fill-current" />}
                    {isTestingEmbed
                      ? t('offersForm.content.testUrlTesting')
                      : t('offersForm.content.testUrl')}
                  </button>
                </FormItem>
              )}
            />
            <OfferEmbedPreviewDialog
              content={embedPreview}
              templateId={templateId}
              visualIdentity={visualIdentity}
              onClose={() => setEmbedPreview(null)}
            />
          </div>
        ) : isStripeCheckoutMode ? (
          <div className="w-full">
            <FormField
              name={`stripePrices${offerType}`}
              control={form.control}
              render={({ field, fieldState }) => (
                <FormItem>
                  <StripePricesField
                    workspaceId={workspaceId}
                    value={field.value ?? []}
                    onChange={(options) => {
                      field.onChange(options);
                      void form.trigger(`stripePrices${offerType}`);
                    }}
                    disabled={locked}
                    unavailable={stripePaymentAvailability === 'UNAVAILABLE' && !fieldState.isDirty}
                    error={fieldState.error?.message}
                  />
                </FormItem>
              )}
            />
          </div>
        ) : (
          <div className="w-full">
            <FormField
              name={`buttonLink${offerType}`}
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <UrlVariableInput
                    className="w-full"
                    label={t('offersForm.content.buttonLinkLabel')}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                  />
                  {field.value && !field.value.trim().startsWith('https://') ? (
                    <p className="text-left text-red-500 text-sm">
                      {t('offersForm.validation.buttonLinkInvalidUrl')}
                    </p>
                  ) : (
                    <FormMessage className="text-left text-red-500 text-sm" />
                  )}
                  <button
                    type="button"
                    onClick={handleTestOfferUrl}
                    disabled={isTestingLink || !templateId || locked}
                    className="mt-2 self-start bg-primary/10 text-primary rounded-lg py-2 px-3 border border-primary/20 cursor-pointer hover:bg-primary/20 h-10 flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      templateId
                        ? t('offersForm.content.testUrlTooltip')
                        : t('offersForm.content.testUrlNotSaved')
                    }
                  >
                    {isTestingLink ? <Loader /> : <Play className="size-4 fill-current" />}
                    {isTestingLink
                      ? t('offersForm.content.testUrlTesting')
                      : t('offersForm.content.testUrl')}
                  </button>
                </FormItem>
              )}
            />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between ">
        <Label>{t('offersForm.content.showImageLabel')}</Label>
        <Switch
          checked={form.watch(`showImage${offerType}`)}
          onCheckedChange={(checked) => {
            form.setValue(`showImage${offerType}`, checked);
            form.trigger(`img${offerType}`);
          }}
        />
      </div>

      <FormField
        name={`img${offerType}`}
        control={form.control}
        render={() => (
          <FormItem>
            <div className="relative">
              <input
                type="file"
                id={fileInputId}
                className="sr-only"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setSelectedFile(file);
                    form.setValue(`img${offerType}`, file);
                    form.setValue(`showImage${offerType}`, true);
                  }
                }}
              />
              <div className="relative">
                <Label
                  htmlFor={fileInputId}
                  className="p-4 bg-white rounded-2xl flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className="size-10 bg-medium rounded-lg flex items-center justify-center">
                      <img
                        src="/assets//attach.svg"
                        alt={t('offersForm.content.fileInput.attachIconAlt')}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <span>
                        {selectedFile
                          ? selectedFile.name
                          : form.watch(`img${offerType}`) &&
                              typeof form.watch(`img${offerType}`) === 'string' &&
                              (form.watch(`img${offerType}`) as string).trim() !== ''
                            ? t('offersForm.content.fileInput.existingFile')
                            : t('offersForm.content.fileInput.selectFile')}
                      </span>
                      <p className="text-sm text-gray-tertiary">
                        {selectedFile
                          ? `${(selectedFile.size / 1024 / 1024).toFixed(1)}mb`
                          : form.watch(`img${offerType}`) &&
                              typeof form.watch(`img${offerType}`) === 'string' &&
                              (form.watch(`img${offerType}`) as string).trim() !== ''
                            ? t('offersForm.content.fileInput.existingFileDescription')
                            : t('offersForm.content.fileInput.noFileSelected')}
                      </p>
                    </div>
                  </div>
                </Label>
                {(selectedFile ||
                  (form.watch(`img${offerType}`) &&
                    typeof form.watch(`img${offerType}`) === 'string' &&
                    (form.watch(`img${offerType}`) as string).trim() !== '')) && (
                  <button
                    type="button"
                    className="absolute top-1/2 -translate-y-1/2 right-6 z-10 flex items-center justify-center rounded-md bg-transparent p-1 text-foreground hover:opacity-80"
                    aria-label={t('offersForm.deleteImage')}
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const imgField = `img${offerType}` as const;
                      const imgVal = form.getValues(imgField);
                      const hasPersistedImage = typeof imgVal === 'string' && imgVal.trim() !== '';

                      if (
                        hasPersistedImage &&
                        workspaceId != null &&
                        webinarId != null &&
                        templateId != null
                      ) {
                        try {
                          await dispatch(
                            deleteOfferTemplateContentImage({
                              workspaceId,
                              webinarId,
                              templateId,
                              contentType: offerType,
                            }),
                          ).unwrap();
                        } catch {
                          toast.error(t('offersFormExtra.imageDeleteError'));
                          return;
                        }
                      }

                      setSelectedFile(null);
                      form.setValue(`showImage${offerType}`, false);
                      form.setValue(imgField, undefined);
                      form.trigger(imgField);
                    }}
                  >
                    <Trash2 className="size-6 shrink-0" aria-hidden />
                  </button>
                )}
              </div>
            </div>
            <FormMessage className="text-left text-red-500 text-sm" />
          </FormItem>
        )}
      />

      <div className="flex flex-col pt-8 space-y-4">
        <Title level={2}>{t('offersForm.content.colorsTitle')}</Title>

        <div className="grid lg:grid-cols-2 gap-4">
          {/* background */}
          <ColorSelector
            form={form}
            offerType={offerType}
            colorsArray={colorsArray}
            colorFieldName={`backgroundColor${offerType}`}
            customColorFieldName={`customBackgroundColor${offerType}`}
            label={t('offersForm.content.backgroundColorLabel')}
            customColorLabel={t('offersForm.content.colors.custom')}
            getFinalColor={(colorType) => getFinalColor(colorType as `backgroundColor${'A' | 'B'}`)}
            getStyle={getBackgroundStyle}
          />

          {/* Borders */}
          <ColorSelector
            form={form}
            offerType={offerType}
            colorsArray={colorsArray}
            colorFieldName={`borderColor${offerType}`}
            customColorFieldName={`customBorderColor${offerType}`}
            label={t('offersForm.content.borderColorLabel')}
            customColorLabel={t('offersForm.content.colors.custom')}
            noBorderLabel={t('offersForm.content.colors.noBorder')}
            showNoBorder={true}
            getFinalColor={(colorType) => getFinalColor(colorType as `borderColor${'A' | 'B'}`)}
            getStyle={(colorId, customColor) => {
              if (colorId === 'no-border') {
                return { backgroundColor: 'white' };
              }
              return getBackgroundStyle(colorId, customColor);
            }}
          />

          {/* Title */}
          <ColorSelector
            form={form}
            offerType={offerType}
            colorsArray={arrayFontColors.map((color) => ({
              id: color.value,
              name: color.name,
              value: color.value,
            }))}
            colorFieldName={`titleColor${offerType}`}
            label={t('offersForm.content.titleColorLabel')}
            showCustomColor={false}
            defaultValue="#FFFFFF"
          />

          {/* Text */}
          <ColorSelector
            form={form}
            offerType={offerType}
            colorsArray={arrayFontColors.map((color) => ({
              id: color.value,
              name: color.name,
              value: color.value,
            }))}
            colorFieldName={`textColor${offerType}`}
            label={t('offersForm.content.textColorLabel')}
            showCustomColor={false}
            defaultValue="#FFFFFF"
          />

          {/* background btn */}
          <ColorSelector
            form={form}
            offerType={offerType}
            colorsArray={colorsArray}
            colorFieldName={`buttonColor${offerType}`}
            customColorFieldName={`customButtonColor${offerType}`}
            label={t('offersForm.content.buttonBackgroundColorLabel')}
            customColorLabel={t('offersForm.content.colors.custom')}
            getFinalColor={(colorType) => getFinalColor(colorType as `buttonColor${'A' | 'B'}`)}
            getStyle={getBackgroundStyle}
          />

          {/* Button Text */}
          <ColorSelector
            form={form}
            offerType={offerType}
            colorsArray={arrayFontColors.map((color) => ({
              id: color.value,
              name: color.name,
              value: color.value,
            }))}
            colorFieldName={`buttonTextColor${offerType}`}
            label={t('offersForm.content.buttonTextColorLabel')}
            showCustomColor={false}
            defaultValue="#FFFFFF"
          />
        </div>
      </div>

      <Accordion type="single" collapsible defaultValue="item-1">
        <AccordionItem value="item-1" className="bg-medium p-0 pt-8 ">
          <AccordionTrigger isChevron className=" p-0">
            <Title level={2}>{t('offersForm.content.previewTitle')}</Title>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col items-center gap-4  bg-medium pt-4 px-0 ">
            <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-left">{t('offersFormExtra.desktop')}</p>
                <div
                  className="w-full max-w-[408px] flex flex-col gap-4 p-4 rounded-2xl border-2"
                  style={{
                    ...getBackgroundStyle(
                      form.watch(`backgroundColor${offerType}`) || 'accent',
                      form.watch(`customBackgroundColor${offerType}`),
                    ),
                    ...getBorderStyle(
                      form.watch(`borderColor${offerType}`) || 'accent',
                      form.watch(`customBorderColor${offerType}`),
                    ),
                  }}
                >
                  {form.watch(`showImage${offerType}`) && previewImageUrl && (
                    <img
                      src={previewImageUrl}
                      alt={t('offersForm.content.previewImageAlt')}
                      className="rounded-lg w-full max-h-56 h-full object-cover object-center"
                    />
                  )}
                  <div className="flex flex-col gap-1">
                    <Title level={2} className="text-left text-base lg:text-[20px]">
                      <span style={{ color: form.watch(`titleColor${offerType}`) }}>
                        {form.watch(`title${offerType}`)}
                      </span>
                    </Title>
                    <p
                      className="text-left text-sm"
                      style={{ color: form.watch(`textColor${offerType}`) }}
                    >
                      {form.watch(`subTitle${offerType}`)}
                    </p>
                    <p
                      className="text-left text-sm"
                      style={{ color: form.watch(`textColor${offerType}`) }}
                    >
                      {form.watch(`description${offerType}`)}
                    </p>
                  </div>
                  <Button
                    aria-label={form.watch(`buttonLabel${offerType}`)}
                    title={form.watch(`buttonLabel${offerType}`)}
                    className="w-full justify-center"
                    style={{
                      ...getBackgroundStyle(
                        form.watch(`buttonColor${offerType}`) || 'accent',
                        form.watch(`customButtonColor${offerType}`),
                      ),
                      color: form.watch(`buttonTextColor${offerType}`),
                    }}
                  >
                    {form.watch(`buttonLabel${offerType}`)}
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-left">{t('offersFormExtra.mobile')}</p>
                <div className="w-full max-w-[360px] rounded-[28px] ">
                  <div
                    className={`flex flex-col w-full items-start justify-center gap-2 p-3 rounded-2xl border-2`}
                    style={{
                      ...getBackgroundStyle(
                        form.watch(`backgroundColor${offerType}`) || 'accent',
                        form.watch(`customBackgroundColor${offerType}`),
                      ),
                      ...getBorderStyle(
                        form.watch(`borderColor${offerType}`) || 'accent',
                        form.watch(`customBorderColor${offerType}`),
                      ),
                    }}
                  >
                    <div className="flex flex-col w-full gap-1 flex-1">
                      <Title level={2} className="text-center text-base">
                        <span style={{ color: form.watch(`titleColor${offerType}`) }}>
                          {form.watch(`title${offerType}`)}
                        </span>
                      </Title>
                      <p
                        className="text-center text-sm"
                        style={{ color: form.watch(`textColor${offerType}`) }}
                      >
                        {form.watch(`subTitle${offerType}`)}
                      </p>
                      <p
                        className="text-center text-xs"
                        style={{ color: form.watch(`textColor${offerType}`) }}
                      >
                        {form.watch(`description${offerType}`)}
                      </p>
                    </div>
                    <div className="w-full">
                      <Button
                        aria-label={form.watch(`buttonLabel${offerType}`)}
                        title={form.watch(`buttonLabel${offerType}`)}
                        className="w-full"
                        style={{
                          ...getBackgroundStyle(
                            form.watch(`buttonColor${offerType}`) || 'accent',
                            form.watch(`customButtonColor${offerType}`),
                          ),
                          color: form.watch(`buttonTextColor${offerType}`),
                        }}
                      >
                        {form.watch(`buttonLabel${offerType}`)}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
